import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys, client } from "queryClient";
import { StockTask } from "types";
import { getRunningOrPendingStockTaskList } from "modules/stocktasks/api";

export const useStockTaskRunningList = () => {
  const [stockTaskList, setStockTaskList] = useState<StockTask[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: [QueryKeys.StockTasks, "running"],
    queryFn: async () => {
      const stockTaskList = await getRunningOrPendingStockTaskList();

      return stockTaskList;
    },
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  });

  useEffect(() => {
    if (data) {
      setStockTaskList(data);
    }
  }, [data]);

  useEffect(() => {
    // Subscribe to creation of StockTask
    const createSub = client.models.StockTask.onCreate().subscribe({
      next: (data) => {
        console.log("Create StockTask", data);
        setStockTaskList((prev) => [data, ...prev]);
      },
      error: (error) => console.warn(error),
    });

    // Subscribe to update of StockTask
    const updateSub = client.models.StockTask.onUpdate().subscribe({
      next: (data) => {
        console.log("Update StockTask", data);
        setStockTaskList((prev) =>
          prev.map((item) => (item.id === data.id ? data : item)),
        );
      },
      error: (error) => console.warn(error),
    });

    // Subscribe to deletion of StockTask
    const deleteSub = client.models.StockTask.onDelete().subscribe({
      next: (data) => {
        console.log("Delete StockTask", data);
        setStockTaskList((prev) => prev.filter((item) => item.id !== data.id));
      },
      error: (error) => console.warn(error),
    });

    return () => {
      createSub.unsubscribe();
      updateSub.unsubscribe();
      deleteSub.unsubscribe();
    };
  }, []);

  const runningStockTask = stockTaskList.some(
    (stockTask) =>
      stockTask.status === "Running" || stockTask.status === "Pending",
  );

  return {
    stockTaskList,
    runningStockTask,
    isLoading,
  };
};
